import type { Query, QueryClient } from '@tanstack/react-query'
import { assessmentTemplatesQueryKeys, jobApplicationsQueryKeys, jobsQueryKeys } from '@/constants/cache-keys.contant'

/**
 * Head element of a generated query key — `createQueryKey` in
 * `src/generated/api-client/@tanstack/react-query.gen.ts` builds `[{ _id, baseURL, tags?, path?, query? }]`,
 * so matching `_id` catches every cached page/sort/filter variant of an operation.
 */
type GeneratedKeyHead = { _id?: unknown; path?: Record<string, unknown> }

const operationPredicate =
    (operationId: string, path?: Record<string, string>) =>
    (query: Query): boolean => {
        const head = query.queryKey[0] as GeneratedKeyHead | undefined
        if (head?._id !== operationId) return false
        return !path || Object.entries(path).every(([key, value]) => head.path?.[key] === value)
    }

/** Invalidate every cached page of one generated operation, regardless of params. */
export function invalidateOperation(queryClient: QueryClient, operationId: string) {
    return queryClient.invalidateQueries({ predicate: operationPredicate(operationId) })
}

/**
 * Jobs list: the generated `getJobs` grid pages plus the legacy `jobs` prefix
 * (`jobsQueryKeys.list`/`detail` are still used by non-grid queries).
 */
export function invalidateJobsList(queryClient: QueryClient) {
    return Promise.all([invalidateOperation(queryClient, 'getJobs'), queryClient.invalidateQueries({ queryKey: jobsQueryKeys.all })])
}

/**
 * Job applications: the generated `getJobApplications` / `getJobApplicationsProcessing` grid pages plus the
 * legacy `jobApplications` prefix (detail/locations/tab-count queries) and the old `addCandidates` grid key.
 */
export function invalidateJobApplications(queryClient: QueryClient) {
    return Promise.all([
        invalidateOperation(queryClient, 'getJobApplications'),
        invalidateOperation(queryClient, 'getJobApplicationsProcessing'),
        queryClient.invalidateQueries({ queryKey: jobApplicationsQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['addCandidates'] }),
    ])
}

/**
 * Candidate matching (talent pool grid): every cached `getJobsIdCandidateMatching` page,
 * optionally narrowed to one job's path param.
 */
export function invalidateCandidateMatching(queryClient: QueryClient, jobId?: number | string) {
    return queryClient.invalidateQueries({
        predicate: operationPredicate('getJobsIdCandidateMatching', jobId !== undefined ? { id: String(jobId) } : undefined),
    })
}

/**
 * Assessment templates + competencies after a template save: generated template/param list and
 * detail pages, plus the legacy `assessmentTemplates` prefix still used by the drawer flow.
 * Competency detail is included because a save rewrites shared competency Q&A and usage.
 */
export function invalidateAssessmentTemplates(queryClient: QueryClient) {
    return Promise.all([
        invalidateOperation(queryClient, 'getAssessmentTemplates'),
        invalidateOperation(queryClient, 'getAssessmentTemplatesId'),
        invalidateOperation(queryClient, 'getAssessmentParams'),
        invalidateOperation(queryClient, 'getAssessmentParamsId'),
        queryClient.invalidateQueries({ queryKey: assessmentTemplatesQueryKeys.all }),
    ])
}
